import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Music4, Heart, MessageCircle, Megaphone, CheckCheck } from "lucide-react";
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from "@/lib/api";

const ICONS = { new_song: Music4, reaction: Heart, reply: MessageCircle, announcement: Megaphone };
const COLORS = { new_song: "#B45309", reaction: "#FF6B6B", reply: "#0284C7", announcement: "#9333EA" };
const BG = { new_song: "#FEF3C7", reaction: "#FFE5E5", reply: "#E0F2FE", announcement: "#F3E8FF" };

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = () => fetchNotifications().then(setData).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const readAll = async () => {
    await markAllNotificationsRead();
    setData((d) => ({ ...d, unread: 0, items: d.items.map((i) => ({ ...i, read: true })) }));
  };

  const readOne = async (notifId) => {
    await markNotificationRead(notifId).catch(() => {});
    setData((d) => ({
      ...d,
      unread: Math.max(0, d.unread - 1),
      items: d.items.map((i) => (i.notif_id === notifId ? { ...i, read: true } : i)),
    }));
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell-btn"
        aria-label="Notifications"
        className="relative grid h-10 w-10 place-items-center rounded-full border border-[#E2DDD0] bg-white text-slate-600 transition-transform hover:scale-105"
      >
        <Bell className="h-4.5 w-4.5 h-[18px] w-[18px]" />
        {data.unread > 0 && (
          <span
            className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-[#FF6B6B] px-1 text-[10px] font-bold text-white"
            data-testid="notif-unread-badge"
          >
            {data.unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            className="absolute right-0 mt-2 w-80 overflow-hidden rounded-3xl border border-[#E2DDD0] bg-white shadow-xl"
            data-testid="notif-dropdown"
          >
            <div className="flex items-center justify-between border-b border-[#F8F5EC] px-4 py-3">
              <p className="font-display text-sm font-bold text-slate-900">Notifications</p>
              {data.unread > 0 && (
                <button
                  onClick={readAll}
                  data-testid="notif-read-all-btn"
                  className="inline-flex items-center gap-1 text-xs font-bold text-[#0284C7] hover:underline"
                >
                  <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {data.items.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notif-empty">
                  Nothing yet — new songs and community love will appear here.
                </p>
              ) : (
                data.items.map((n) => {
                  const Icon = ICONS[n.type] || Megaphone;
                  return (
                    <button
                      key={n.notif_id}
                      onClick={() => readOne(n.notif_id)}
                      data-testid={`notif-item-${n.notif_id}`}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[#F8F5EC] ${
                        n.read ? "opacity-55" : ""
                      }`}
                    >
                      <span
                        className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full"
                        style={{ backgroundColor: BG[n.type] || "#F8F5EC" }}
                      >
                        <Icon className="h-4 w-4" style={{ color: COLORS[n.type] || "#64748B" }} />
                      </span>
                      <span>
                        <span className="block text-sm font-bold text-slate-800">{n.title}</span>
                        <span className="mt-0.5 block text-xs text-slate-500 leading-snug">{n.body}</span>
                      </span>
                      {!n.read && <span className="ml-auto mt-2 h-2 w-2 shrink-0 rounded-full bg-[#FF6B6B]" />}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
